import { getType } from '@turf/invariant';
import truncate from '@turf/truncate';
import union from '@turf/union';
import * as helpers from '@turf/helpers';

import { WATERBODIES_LAYER_ID } from '../../components/layers/waterbodies-transparent-layer/WaterbodiesTransparentLayer';
import { MAP_COORDS_PRECISION } from '../../../common/utils/constants/constants';

const getRenderedFeaturesFromWaterLayer = (
  map: mapboxgl.Map,
): mapboxgl.MapboxGeoJSONFeature[] | null => {
  if (!map.getLayer(WATERBODIES_LAYER_ID)) {
    return null;
  }

  return map.queryRenderedFeatures(undefined, {
    // there is no sense in searching for waterways as long as it is line-like feature, so if point is on line it is already snapped to it
    layers: [WATERBODIES_LAYER_ID],
  });
};

/**
 * Function to get currently rendered water polygon features from map or null if there is no water layer
 * @param map `mapboxgl.Map` - map object to get rendered water layer features from
 * @returns Feature<Polygon, Properties>[] - list of currently rendered on map water features or null if there is no water layer rendered
 */
export const getMapWaterPolygons = (map: mapboxgl.Map) => {
  const waterFeatures = getRenderedFeaturesFromWaterLayer(map);

  if (!waterFeatures) {
    return null;
  }

  // https://github.com/Turfjs/turf/issues/1983
  const truncateOptions = {
    precision: MAP_COORDS_PRECISION,
  };
  const polygonFeatures = waterFeatures
    .filter((feature) => getType(feature).includes('Polygon'))
    .map((feature) =>
      truncate(feature as helpers.Feature<helpers.Polygon>, truncateOptions),
    );

  return polygonFeatures;
};

export const unionMapWaterPolygons = (
  waterPolygons: helpers.Feature<helpers.Polygon, helpers.Properties>[] | null,
) => {
  if (!waterPolygons?.length) {
    return helpers.polygon([]);
  }
  const truncateOptions = {
    precision: MAP_COORDS_PRECISION,
  };
  const unifiedPolygon = waterPolygons.reduce(
    (acc, polygon) =>
      truncate(
        union(acc, polygon) as helpers.Feature<
          helpers.Polygon,
          helpers.Properties
        >,
        truncateOptions,
      ),
    waterPolygons[0],
  );
  return unifiedPolygon;
};
/**
 * Function to get multipolygon of polygonal water features rendered on the map
 * @param map - map instance to query for rendered water polygons
 * @returns Feature<Polygon, Properties> - polygon/multipolygon union feature containing all the water features rendered on the map
 */
export const getMapWaterUnionPolygon = (map: mapboxgl.Map) => {
  const waterPolygons = getMapWaterPolygons(map);
  return unionMapWaterPolygons(waterPolygons);
};
